import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface CompanyDelivery {
  id: number;
  name: string;
  country: string;
  city: string;
  postal_code: string;
  street_name: string;
  house_number: string;
  instructions: string | null;
}

export interface CompanyDeliveryInput {
  company_id: number;
  name: string;
  country: string;
  city: string;
  postal_code: string;
  street_name: string;
  house_number: string;
  instructions?: string | null;
}

export const useCompanyDelivery = (companyId?: number | null) => {
  const [deliveryAddresses, setDeliveryAddresses] = useState<CompanyDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeliveryAddresses = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('company_delivery')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false });

        if (error) {
          if (error.code === "PGRST116" || error.message?.includes("JSON object requested, multiple (or no) rows returned")) {
            console.warn(`No delivery addresses found for company ID ${companyId}`);
            setDeliveryAddresses([]);
            setLoading(false);
            return;
          }
          throw error;
        }
        setDeliveryAddresses(data || []);
      } catch (err) {
        console.error('Error fetching delivery addresses:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch delivery addresses');
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveryAddresses();
  }, [companyId]);

  const saveCompanyDelivery = async (delivery: CompanyDeliveryInput) => {
    try {
      const { data, error } = await supabase
        .from('company_delivery')
        .insert(delivery)
        .select()
        .single();

      if (error) throw error;
      setDeliveryAddresses(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error saving delivery address:', err);
      throw err;
    }
  };

  const deleteCompanyDelivery = async (id: number) => {
    try {
      const { error } = await supabase
        .from('company_delivery')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setDeliveryAddresses(prev => prev.filter(address => address.id !== id));
      
      return true;
    } catch (err) {
      console.error('Error deleting delivery address:', err);
      throw err;
    }
  };

  return { 
    deliveryAddresses, 
    loading, 
    error, 
    saveCompanyDelivery,
    deleteCompanyDelivery
  };
}; 